import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { Notebook } from "@learningblocks/simulations/shared-components";
import { ReactDrawer } from "@learningblocks/storybook-components";
import { Utilities } from "@learningblocks/exp-constants";
const { getLocalStorageItem, NOTEBOOK, TOUR_PROPERTIES } = Utilities;
import { CommonSectionContent } from "./CommonSectionContent";
import { DesignSectionContent } from "./DesignSectionContent";
import { ProtocolSectionContent } from "./ProcotolSectionContent";
import { Log } from "@learningblocks/shared-components/lib/EventLogger";
import { HotspotCommonSectionContent } from "@learningblocks/simulations/hotspot-sim-components";
import { SpreadsheetProtocolSection } from "@learningblocks/simulations/spreadsheet-sim-components";
import "./style.scss";

/**
 * NotebookWrapper:
 * - Used to display the Nootbook of experimental design [ Web view]
 */
export function NotebookWrapper(props) {
  const sectionType = () => props.simType ?? props.currentMenu?.value ?? null;

  const isDesignSection = () =>
    props.currentMenu.value === NOTEBOOK.SECTION.DESIGN;

  const isProtocolSection = () =>
    props.currentMenu.value === NOTEBOOK.SECTION.PROTOCOL;

  const isExperimentalDesignComplete = getLocalStorageItem(
    TOUR_PROPERTIES.IS_EXPERIMENTAL_DESIGN_FINISHED
  );

  useEffect(() => {
    if (isProtocolSection()) document.getElementById("overview")?.focus();
  }, [props.currentMenu]);

  const renderScene = () => {
    switch (sectionType()) {
      case null:
        return null;
      case "SPREADSHEET": {
        const sceneMenus = props.menuList.filter(
          (menu) => menu.chapterType === "Scene"
        );
        return (
          <>
            {sceneMenus.map((menu, index) => (
              <div
                key={index}
                aria-hidden={menu === props.currentMenu ? "true" : "false"}
              >
                <SpreadsheetProtocolSection
                  title={props.title}
                  currentMenu={menu}
                  description={props.description}
                  goToSection={props.onSetChapter}
                  nextMenuValue={props.nextMenuValue}
                  prevMenuValue={props.prevMenuValue}
                  setNotebookVisible={props.onSetCollapse}
                  options={props.options}
                  viewType={props.viewType}
                  onClickInfo={props.startTour}
                  collapsed={props.drawerCollapsed}
                  noDrawerToggle={true}
                  simData={menu.simData}
                  stepData={menu.stepData}
                  includeScene={true}
                  includeProtocol={false}
                />
              </div>
            ))}
          </>
        );
      }
      default:
        return null;
    }
  };

  const renderContent = () => {
    switch (sectionType()) {
      case null:
        return null;
      case "HOTSPOT":
        return (
          <Log section={props.currentMenu.value.toLowerCase()}>
            <HotspotCommonSectionContent
              title={props.title}
              progress={props.progress}
              maxProgress={props.maxProgress}
              currentProgress={props.currentProgress}
              menuList={props.menuList}
              components={props.components}
              currentMenu={props.currentMenu}
              description={props.description}
              goToSection={props.onSetChapter}
              updateProgress={props.onUpdateProgress}
              prevMenuValue={props.prevMenuValue}
              nextMenuValue={props.nextMenuValue}
              setNotebookVisible={props.onSetCollapse}
              collapsed={props.drawerCollapsed}
              updateSceneData={props.onUpdateSceneData}
            />
          </Log>
        );
      case "SPREADSHEET":
        if (props.currentMenu.chapterType === "Scene")
          return (
            <Log section={props.currentMenu.value.toLowerCase()}>
              <SpreadsheetProtocolSection
                title={props.title}
                currentMenu={props.currentMenu}
                description={props.description}
                goToSection={props.onSetChapter}
                nextMenuValue={props.nextMenuValue}
                prevMenuValue={props.prevMenuValue}
                setNotebookVisible={props.onSetCollapse}
                options={props.options}
                viewType={props.viewType}
                onClickInfo={props.startTour}
                collapsed={props.drawerCollapsed}
                noDrawerToggle={true}
                simData={props.currentMenu.simData}
                stepData={props.currentMenu.stepData}
                includeScene={false}
                includeProtocol={true}
              />
            </Log>
          );
        else
          return (
            <Log section={props.currentMenu.value.toLowerCase()}>
              <CommonSectionContent
                title={props.title}
                progress={props.progress}
                maxProgress={props.maxProgress}
                currentProgress={props.currentProgress}
                menuList={props.menuList}
                components={props.components}
                currentMenu={props.currentMenu}
                description={props.description}
                goToSection={props.onSetChapter}
                prevMenuValue={props.prevMenuValue}
                nextMenuValue={props.nextMenuValue}
                setNotebookVisible={props.onSetCollapse}
                collapsed={props.drawerCollapsed}
                noDrawerToggle={true}
                simSpecificComponentMap={props.simSpecificComponentMap}
              />
            </Log>
          );
      case NOTEBOOK.SECTION.DESIGN:
        !isExperimentalDesignComplete && props.startTour();
        return (
          <DesignSectionContent
            title={props.title}
            currentMenu={props.currentMenu}
            menuList={props.menuList}
            description={props.description}
            goToSection={props.onSetChapter}
            prevMenuValue={props.prevMenuValue}
            nextMenuValue={props.nextMenuValue}
            setNotebookVisible={props.onSetCollapse}
            options={props.options}
            viewType={props.viewType}
            onClickInfo={props.startTour}
          />
        );
      case NOTEBOOK.SECTION.PROTOCOL: {
        if (props.scene) {
          return props.scene;
        } else {
          return (
            <ProtocolSectionContent
              title={props.title}
              currentMenu={props.currentMenu}
              description={props.description}
              goToSection={props.onSetChapter}
              nextMenuValue={props.nextMenuValue}
              setNotebookVisible={props.onSetCollapse}
              options={props.options}
              viewType={props.viewType}
              onClickInfo={props.startTour}
              usingNewDesignSystem={props.usingNewDesignSystem}
              maxProgress={props.maxProgress}
              currentProgress={props.currentProgress}
              progress={props.progress}
              menuList={props.menuList}
            />
          );
        }
      }
      default:
        return (
          <Log section={props.currentMenu.value.toLowerCase()}>
            <CommonSectionContent
              title={props.title}
              progress={props.progress}
              maxProgress={props.maxProgress}
              currentProgress={props.currentProgress}
              menuList={props.menuList}
              components={props.components}
              currentMenu={props.currentMenu}
              description={props.description}
              goToSection={props.onSetChapter}
              updateProgress={props.onMutatedUpdateProgress}
              prevMenuValue={props.prevMenuValue}
              nextMenuValue={props.nextMenuValue}
              setNotebookVisible={props.onSetCollapse}
              collapsed={props.drawerCollapsed}
              noDrawerToggle={false}
              simSpecificComponentMap={props.simSpecificComponentMap}
            />
          </Log>
        );
    }
  };

  const renderDrawerCollapsed = () => {
    if (props.drawerCollapsed) {
      return (
        <Notebook.DrawerIcon
          collapsed={true}
          toggleCollapse={props.onSetCollapse}
        />
      );
    } else {
      return null;
    }
  };

  const getShowOverlay = () => !isDesignSection() && !isProtocolSection();

  return (
    <>
      {renderDrawerCollapsed()}
      <ReactDrawer
        showOverlay={getShowOverlay()}
        visible={!props.drawerCollapsed}
      >
        <Notebook.Wrapper
          className={props.className ? `${props.className} book` : "book"}
        >
          {renderScene()}
          {renderContent()}
        </Notebook.Wrapper>
      </ReactDrawer>
    </>
  );
}

NotebookWrapper.propTypes = {
  /**
   * Components in current section to be displayed in Notebook
   */
  components: PropTypes.arrayOf(PropTypes.object),
  /**
   * Details of currently active menu in menu list
   */
  currentMenu: PropTypes.shape({
    /**
     * Index of menu
     */
    number: PropTypes.number,
    /**
     * Title of menu
     */
    title: PropTypes.string,
    /**
     * Value of menu
     */
    value: PropTypes.string,
  }),
  /**
   * Description to be displayed in Notebook Header
   */
  description: PropTypes.string,
  /**
   * Function to set new Chapter
   */
  onSetChapter: PropTypes.func,
  /**
   * Function to update Progress
   */
  onUpdateProgress: PropTypes.func,
  /**
   * Function to update Progress with mutation
   */
  onMutatedUpdateProgress: PropTypes.func,
  /**
   * To show or hide Notebook
   */
  drawerCollapsed: PropTypes.bool,
  /**
   * List of Menu Items
   */
  menuList: PropTypes.arrayOf(
    PropTypes.shape({
      /**
       * Title to display in  menu
       */
      title: PropTypes.string.isRequired,
      /**
       * Boolean specifying if menu is selected
       */
      current: PropTypes.bool.isRequired,
      /**
       * Index number of menu
       */
      number: PropTypes.number.isRequired,
      /**
       * Unique string value of the menu
       */
      value: PropTypes.string.isRequired,
      /**
       * Boolean set when section is visited
       */
      visited: PropTypes.bool.isRequired,
    })
  ),
  /**
   * Value for next-in-line menu
   * NONE value mens no more next section
   */
  nextMenuValue: PropTypes.oneOf([
    "CONTEXT",
    "MATERIALS",
    "PREDICTIONS",
    "TECHNIQUES",
    "DESIGN",
    "PROTOCOL",
    "PROBLEM_SOLVING",
    "RESULTS",
    "REFLECTION",
    "TROUBLESHOOTING",
    "SUMMARY",
    "NONE",
    "PREDICTIONS1",
    "PROTOCOL1",
    "RESULTS1",
    "PREDICTIONS2",
    "PROTOCOL2",
    "RESULTS2",
  ]),
  /**
   * Function to set new isNotebookVisible value
   */
  setNotebookVisible: PropTypes.func,
  /**
   * Title of the Content to display at header
   */
  title: PropTypes.string,
  /**
   * simTpye used to display different headers
   */
  simType: PropTypes.string,
  /**
   * progress used to display progressbar in header
   */
  progress: PropTypes.bool,
  /**
   * maxProgress to be displayed in header
   */
  maxProgress: PropTypes.number,
  /**
   * currentProgress to be displayed in header
   */
  currentProgress: PropTypes.number,
  /**
   * function to start Page Tour
   */
  startTour: PropTypes.func,
  /**
   * ClassName
   */
  className: PropTypes.string,
  /**
   * Map of sim-specific components to be rendered in the notebook
   */
  simSpecificComponentMap: PropTypes.objectOf(PropTypes.elementType),
};

NotebookWrapper.defaultProps = {
  title: "",
  description: "",
};
